import { useState, useCallback, useEffect } from "react"
import { useNavigate, useLocation } from 'react-router-dom'
import Calendar from '../components/calendar/Calendar'
import DayEventsList from '../components/calendar/DayEventsList'
import EventModal from '../components/calendar/EventModal'
import { useOnlineStatus } from '../hooks/useOnlineStatus'
import { useCalendar } from '../hooks/useCalendar'
import { useSyncCalendar } from '../hooks/useSyncCalendar'
import CombinedModal from '../components/calendar/CombinedModal'
import { useEventsStore } from '../stores/eventsStore'
import Layout from '../components/layout/Layout'
import { useCalendarStore } from '../stores/calendarStore'
import { useFamilyStore } from '../stores/familyStore'
import { usePermissions } from '../hooks/usePermissions'
import { format } from 'date-fns'
import logger from '../utils/logger'
import UsageInfoModal from '../components/calendar/UsageInfoModal'

export default function CalendarPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const [currentView] = useState<"care" | "events">("care")
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null)
  const [showEventModal, setShowEventModal] = useState(false)
  const [showCombinedModal, setShowCombinedModal] = useState(false)
  const [showUsageInfo, setShowUsageInfo] = useState(false)
  const [modalDefaultDate, setModalDefaultDate] = useState<Date>(new Date())
  
  const isOnline = useOnlineStatus()
  const { family, isLoading: familyLoading, initialized: familyInitialized } = useFamilyStore()
  // Use real children count from familyStore to avoid race condition
  const realChildrenCount = useFamilyStore(state => state.children.length)
  const { userRole, canEditCalendar } = usePermissions()
  
  const { 
    children, 
    selectedChildId, 
    selectedHome,
    setSelectedChild,
    setSelectedHome,
    updateCalendarEntry,
    isLoading,
    error,
    currentDate,
    entries
  } = useCalendar()
  
  // Init calendar sync
  useSyncCalendar()

  // Expose handler globally for "i" icon in Navbar
  useEffect(() => {
    ;(window as any).__openUsageInfo = () => setShowUsageInfo(true)
    return () => { try { delete (window as any).__openUsageInfo } catch {} }
  }, [])

  // Init home based on user role
  useEffect(() => {
    if (selectedHome === null && userRole) {
      // Auto-select home based on user role
      const defaultHome = userRole === 'parent_a' ? 'home_a' : 
                         userRole === 'parent_b' ? 'home_b' : 'home_a'
      logger.debug(`🏠 Auto-selecting home for role ${userRole}: ${defaultHome}`)
      setSelectedHome(defaultHome)
    }
  }, [selectedHome, userRole, setSelectedHome])

  // If family exists but no real children – redirect to settings
  useEffect(() => {
    if (familyInitialized && !familyLoading && family && realChildrenCount === 0) {
      navigate('/family-settings')
    }
  }, [familyInitialized, familyLoading, family, realChildrenCount, navigate])

  // One-time navigation focus on newly added child
  useEffect(() => {
    const state = location.state as { focusChildId?: string } | null
    if (!state?.focusChildId) return

    const focusChildId = state.focusChildId
    const hasChild = children.some((child) => child.id === focusChildId)

    if (!hasChild) {
      // Wait for children list to update from backend
      return
    }

    logger.debug(`👶 Received focusChildId=${focusChildId} from nav – setting as selected child`)
    setSelectedChild(focusChildId)

    // Clear nav state
    navigate(location.pathname, { replace: true })
  }, [location, children, setSelectedChild, navigate])

  // Simple view change without animation – only interaction mode change
  // View switcher unused – leaving single Care view

  const handleHomeChange = useCallback((home: "home_a" | "home_b") => {
    setSelectedHome(home)
  }, [setSelectedHome])
  
  const handleChildChange = useCallback((childId: string) => {
    setSelectedChild(childId)
  }, [setSelectedChild])
  
  const handleCareDayClick = useCallback((date: Date) => {
    setSelectedDate(date)
    const dateString = format(date, 'yyyy-MM-dd')
    const entries = useCalendarStore.getState().entries
    const dayEntries = entries.filter(entry => entry.date === dateString)
    const dayEntry = dayEntries[0]
    const currentLocation = dayEntry?.location || null
    // Check if events (notes/costs) exist on this day
    try {
      const events = useEventsStore.getState().events
      const hasEvents = events.some(ev => ev.child_id === (selectedChildId || '') && format(new Date(ev.start_at), 'yyyy-MM-dd') === dateString)
      if (hasEvents) {
        // If events exist – only select
        return
      }
    } catch {}
    // 1) Empty cell → assign selected home (as before)
    if (!currentLocation) {
      if (!canEditCalendar) return
      if (selectedHome !== undefined && selectedHome !== null) {
        updateCalendarEntry(dateString, "day", selectedHome)
      }
      return
    }
    // 2) Occupied cell (care/note/cost) → only select
  }, [selectedHome, selectedChildId, updateCalendarEntry, canEditCalendar])

  const handleEventsDayClick = useCallback((date: Date) => {
    setSelectedDate(date)
  }, [])

  const handleDayDoubleClick = useCallback((date: Date) => {
    setSelectedDate(date)
    setModalDefaultDate(date)
    setShowCombinedModal(true)
  }, [])

  

  // If familyStore is still loading, show loading
  if (!familyInitialized || familyLoading) {
    return (
      <Layout 
        currentHome="home_a"
        onHomeChange={() => {}}
        selectedChild=""
        onChildChange={() => {}}
        childrenData={[]}
        isLoading={true}
        currentView={currentView}
        onViewChange={() => {}}
      >
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-slate-600">Checking family membership...</p>
          </div>
        </div>
      </Layout>
    )
  }

  // If user has no family (after full init), show info and link to create family
  // IMPORTANT: Check only familyStore, not isLoading from useCalendar
  if (!family && familyInitialized && !familyLoading) {
    return (
      <Layout 
        currentHome="home_a"
        onHomeChange={() => {}}
        selectedChild=""
        onChildChange={() => {}}
        childrenData={[]}
        isLoading={false}
        currentView={currentView}
        onViewChange={() => {}}
      >
        <div className="max-w-2xl mx-auto px-4 py-12 text-center">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="mb-6">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Welcome to 2homes.app!
              </h2>
              <p className="text-gray-600">
                To start using the calendar, create a family or ask for an invitation to an existing one.
              </p>
            </div>
            
            <div className="space-y-3">
              <button
                onClick={() => window.location.href = '/family-settings'}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors w-full"
              >
                Create a new family
              </button>
              <p className="text-sm text-gray-500">
                Or ask a family member to send an invitation to your email
              </p>
            </div>
          </div>
        </div>
      </Layout>
    )
  }

  return (
    <Layout 
      currentHome={selectedHome || "home_a"}
      onHomeChange={handleHomeChange}
      selectedChild={selectedChildId || ""}
      onChildChange={handleChildChange}
      childrenData={children}
      isLoading={isLoading}
      currentView={currentView}
      onViewChange={undefined}
      onAddEventClick={() => {
        const d = selectedDate || currentDate
        setSelectedDate(d)
        setModalDefaultDate(d)
        setShowCombinedModal(true)
      }}
    >
      {/* Offline banner */}
      {!isOnline && (
        <div className="bg-gradient-to-r from-yellow-400 to-orange-400 text-white px-4 py-2 text-center">
          <span className="text-sm font-medium">
            You are working offline. Changes will be synced when online.
          </span>
        </div>
      )}
      
      {/* Error banner */}
      {error && (
        <div className="bg-gradient-to-r from-red-400 to-pink-400 text-white px-4 py-2 text-center">
          <span className="text-sm font-medium">
            Error: {error}
          </span>
        </div>
      )}

      <div className="max-w-[1600px] mx-auto px-0 sm:px-4 lg:px-8 py-1 sm:py-3">
        {/* Full width calendar */}
        <div className="calendar-full-width">
          {(isLoading && entries.length === 0) ? (
            <div className="flex items-center justify-center h-96">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                <p className="text-slate-600">Loading calendar...</p>
              </div>
            </div>
          ) : (
            <Calendar 
              view="care"
              selectedChild={selectedChildId || ""}
              selectedDate={selectedDate || currentDate}
              onDayClick={currentView === 'events' ? handleEventsDayClick : handleCareDayClick}
              onDayDoubleClick={handleDayDoubleClick}
            />
          )}
        </div>
      </div>

      {/* Day events list below calendar (for both modes) */}
      {selectedChildId && selectedChildId !== 'all-children' && (
        <div className="max-w-[1600px] mx-auto px-0 sm:px-4 lg:px-8 pb-4">
          <DayEventsList
            date={selectedDate || currentDate}
            childId={selectedChildId}
            onAddEvent={(d) => { setModalDefaultDate(d); setSelectedDate(d); setSelectedEventId(null); setShowEventModal(true) }}
            onEditEvent={(eventId, d) => { setModalDefaultDate(d); setSelectedDate(d); setSelectedEventId(eventId); setShowEventModal(true) }}
          />
        </div>
      )}
      
      {/* CombinedModal – Care/Note/Cost on double click */}
      {selectedChildId && selectedChildId !== 'all-children' && selectedDate && (
        <CombinedModal
          isOpen={showCombinedModal}
          onClose={() => setShowCombinedModal(false)}
          defaultDate={selectedDate}
          childId={selectedChildId}
        />
      )}

      {/* Usage Info Modal */}
      <UsageInfoModal isOpen={showUsageInfo} onClose={() => setShowUsageInfo(false)} />

      {/* Event Modal */}
      {selectedChildId && selectedChildId !== 'all-children' && (
        <EventModal
          isOpen={showEventModal}
          onClose={() => { setShowEventModal(false); setSelectedEventId(null) }}
          defaultDate={modalDefaultDate}
          childId={selectedChildId}
          eventId={selectedEventId || undefined}
        />
      )}
    </Layout>
  )
} 
